import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import StatusBadge, { STATUS_OPTIONS } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Timeline from "@/components/Timeline";
import { Layers, Plus, Loader2, Truck, MapPin, Trash2, Package, X, ChevronRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { triggerHaptic } from "@/lib/haptics";
import EmptyState from "@/components/EmptyState";
import TrackingPill from "@/components/TrackingPill";
import PageHeader from "@/components/PageHeader";

const orderLabel = (o) => o.recipient_name || o.user_name || o.customer_name || "—";

function OrderPicker({ orders, selected, onToggle, emptyText }) {
  if (!orders.length) return <div className="text-sm text-muted-foreground py-4 text-center">{emptyText}</div>;
  return (
    <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y" data-testid="batch-order-picker">
      {orders.map((o) => (
        <label key={o.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/40" data-testid={`batch-pick-${o.tracking_number}`}>
          <input type="checkbox" className="h-4 w-4 rounded border-border" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} data-testid={`batch-pick-checkbox-${o.tracking_number}`} />
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-xs text-muted-foreground">{o.tracking_number}</div>
            <div className="text-sm font-medium truncate">{orderLabel(o)} · {o.destination_country}</div>
          </div>
          <StatusBadge status={o.status} />
        </label>
      ))}
    </div>
  );
}

export default function BatchesPanel() {
  const [batches, setBatches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [batchNumber, setBatchNumber] = useState("");
  const [pick, setPick] = useState([]);

  const [detail, setDetail] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addPick, setAddPick] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", location: "", date: "", note: "" });
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverForm, setDeliverForm] = useState({ location: "", received_by: "", date: "" });
  const { user } = useAuth();
  const canDeliver = user?.role === "admin" || user?.role === "operations";
  const [trackForm, setTrackForm] = useState({ domestic_courier: "", pickup_tracking_number: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [b, o] = await Promise.all([api.get("/batches"), api.get("/orders")]);
      setBatches(b.data);
      setOrders(o.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const available = orders.filter((o) => !o.batch_id && !o.warehouse_received && o.status !== "Delivered" && o.status !== "Cancelled");
  const togglePick = (id) => setPick((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAddPick = (id) => setAddPick((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const openDetail = async (b) => {
    try {
      const { data } = await api.get(`/batches/${b.id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const refreshDetail = async (id) => {
    const { data } = await api.get(`/batches/${id}`);
    setDetail(data);
    load();
  };

  const createBatch = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/batches", { batch_number: batchNumber, order_ids: pick });
      toast.success(`Batch ${data.batch_number} created with ${data.added} shipment(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setCreateOpen(false); setBatchNumber(""); setPick([]); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doBatchStatus = async () => {
    if (!statusForm.status) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/batches/${detail.id}/status`, statusForm);
      toast.success(`Status applied to ${data.updated} shipment(s)${data.skipped ? `, ${data.skipped} skipped (locked)` : ""}`);
      setStatusOpen(false); refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doBatchDeliver = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/batches/${detail.id}/deliver`, deliverForm);
      toast.success(`${data.updated} shipment(s) marked delivered${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setDeliverOpen(false); refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doBatchTracking = async () => {
    if (!trackForm.domestic_courier) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/batches/${detail.id}/tracking`, trackForm);
      toast.success(`Tracking applied to ${data.updated} shipment(s)${data.skipped ? `, ${data.skipped} skipped (locked)` : ""}`);
      setTrackOpen(false); refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addToBatch = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/batches/${detail.id}/orders`, { order_ids: addPick });
      toast.success(`Added ${data.added} shipment(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setAddOpen(false); setAddPick([]); refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeOrder = async (orderId) => {
    try {
      await api.delete(`/batches/${detail.id}/orders/${orderId}`);
      toast.success("Removed from batch");
      refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm(`Delete batch ${b.batch_number}? Shipments will be released (not deleted).`)) return;
    try {
      await api.delete(`/batches/${b.id}`);
      toast.success("Batch deleted");
      setDetail(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="batches-panel">
      <PageHeader eyebrow="Grouping" title="Batches" subtitle="Group shipments under one batch number and update status or tracking for the whole batch at once."
        actions={<Button size="lg" className="shine-hover" onClick={() => { setBatchNumber(""); setPick([]); setCreateOpen(true); }} data-testid="create-batch-button"><Plus className="h-4 w-4" /> Create Batch</Button>} />

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="panel h-28 animate-pulse bg-slate-100/60" />)}</div>
      ) : batches.length === 0 ? (
        <EmptyState icon={Layers} title="No batches yet" hint="Create a batch to group shipments and update them together." testId="batches-empty"
          action={<Button onClick={() => { setBatchNumber(""); setPick([]); setCreateOpen(true); }}><Plus className="h-4 w-4" /> Create Batch</Button>} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {batches.map((b, i) => (
            <button key={b.id} onClick={() => { triggerHaptic("tap"); openDetail(b); }} style={{ "--i": Math.min(i, 8) }}
              className="panel hover-lift fade-up group relative overflow-hidden p-5 text-left" data-testid={`batch-card-${b.batch_number}`}>
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary to-cyan-400" />
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3"><Layers className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="truncate font-display text-lg font-bold tracking-tight">{b.batch_number}</div>
                    <div className="text-xs text-muted-foreground">Created by {b.created_by_name || b.created_by_role}</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary" data-testid={`batch-count-${b.batch_number}`}>{b.order_count} shipment{b.order_count === 1 ? "" : "s"}</span>
                <span className="text-[11px] font-mono-label uppercase tracking-widest text-muted-foreground">Open</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create batch */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-batch-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Create Batch</DialogTitle>
            <DialogDescription>Name your batch and pick the shipments to include.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Batch Number <span className="text-muted-foreground font-normal">(leave empty to auto-generate)</span></Label>
              <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="e.g. BATCH-DEL-01" data-testid="batch-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Shipments <span className="text-muted-foreground font-normal">({pick.length} selected)</span></Label>
              <OrderPicker orders={available} selected={pick} onToggle={togglePick} emptyText="No eligible shipments to add." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createBatch} disabled={saving} data-testid="confirm-create-batch">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Batch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch detail */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> {detail.batch_number}</DialogTitle>
                <DialogDescription>{detail.order_count} shipment(s) · updates apply to all except those already received at the hub.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => { setStatusForm({ status: "", location: "", date: new Date().toISOString().slice(0, 10), note: "" }); setStatusOpen(true); }} data-testid="batch-update-status-button"><MapPin className="h-4 w-4 mr-1" /> Update Status</Button>
                <Button size="sm" variant="outline" onClick={() => { setTrackForm({ domestic_courier: "", pickup_tracking_number: "" }); setTrackOpen(true); }} data-testid="batch-update-tracking-button"><Truck className="h-4 w-4 mr-1" /> Update Tracking</Button>
                {canDeliver && <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => { setDeliverForm({ location: "", received_by: "", date: new Date().toISOString().slice(0, 10) }); setDeliverOpen(true); }} data-testid="batch-deliver-button"><CheckCircle2 className="h-4 w-4 mr-1" /> Mark Delivered</Button>}
                <Button size="sm" variant="outline" onClick={() => { setAddPick([]); setAddOpen(true); }} data-testid="batch-add-orders-button"><Plus className="h-4 w-4 mr-1" /> Add Shipments</Button>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 ml-auto" onClick={() => deleteBatch(detail)} data-testid="batch-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete Batch</Button>
              </div>
              <div className="rounded-lg border border-border divide-y" data-testid="batch-orders-list">
                {(detail.orders || []).length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">No shipments in this batch.</div>
                ) : (detail.orders || []).map((o) => (
                  <div key={o.id} className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-primary/[.025]" data-testid={`batch-order-${o.tracking_number}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Package className="h-4 w-4" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap"><TrackingPill tn={o.tracking_number} />{o.booked_by_courier && <span className="rounded-full bg-orange-50 border border-orange-200 px-1.5 text-[9px] font-bold uppercase text-orange-700" data-testid={`batch-booked-tag-${o.tracking_number}`}>Booked by you</span>}</div>
                      <div className="text-sm font-medium truncate">{orderLabel(o)} · {o.destination_country}</div>
                      {o.domestic_courier && <div className="text-xs text-muted-foreground truncate">{o.domestic_courier}{o.pickup_tracking_number ? ` · ${o.pickup_tracking_number}` : ""}</div>}
                    </div>
                    <StatusBadge status={o.status} />
                    <Button size="sm" variant="outline" onClick={() => setOrderDetail(o)} data-testid={`batch-view-order-${o.tracking_number}`}>View</Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => removeOrder(o.id)} data-testid={`batch-remove-order-${o.tracking_number}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch status */}
      <Dialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <DialogContent data-testid="batch-deliver-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Mark Batch Delivered</DialogTitle>
            <DialogDescription>{detail?.batch_number} · every shipment in this batch is marked Delivered (cancelled or already delivered ones are skipped) and a Proof of Delivery is generated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Delivery Location <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={deliverForm.location} onChange={(e) => setDeliverForm((f) => ({ ...f, location: e.target.value }))} placeholder="Defaults to each receiver's city" data-testid="batch-deliver-location" /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Received By <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={deliverForm.received_by} onChange={(e) => setDeliverForm((f) => ({ ...f, received_by: e.target.value }))} data-testid="batch-deliver-receivedby" /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={deliverForm.date} onChange={(e) => setDeliverForm((f) => ({ ...f, date: e.target.value }))} data-testid="batch-deliver-date" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="success" onClick={doBatchDeliver} disabled={saving} data-testid="confirm-batch-deliver">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Delivered</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent data-testid="batch-status-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Update Batch Status</DialogTitle>
            <DialogDescription>Applies to every shipment in this batch that isn't yet received at the hub.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Status</Label><Select value={statusForm.status} onValueChange={(v) => setStatusForm((f) => ({ ...f, status: v }))}><SelectTrigger data-testid="batch-status-input"><SelectValue placeholder="Choose stage" /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Location <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={statusForm.location} onChange={(e) => setStatusForm((f) => ({ ...f, location: e.target.value }))} data-testid="batch-status-location" /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={statusForm.date} onChange={(e) => setStatusForm((f) => ({ ...f, date: e.target.value }))} data-testid="batch-status-date" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))} data-testid="batch-status-note" /></div>
          </div>
          <DialogFooter><Button onClick={doBatchStatus} disabled={saving || !statusForm.status} data-testid="confirm-batch-status">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Apply to Batch</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch tracking */}
      <Dialog open={trackOpen} onOpenChange={setTrackOpen}>
        <DialogContent data-testid="batch-tracking-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Update Batch Tracking</DialogTitle>
            <DialogDescription>Applies to every shipment in this batch that isn't yet received at the hub.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Courier Name</Label><Input value={trackForm.domestic_courier} onChange={(e) => setTrackForm((f) => ({ ...f, domestic_courier: e.target.value }))} placeholder="e.g. Delhivery, BlueDart, DTDC" data-testid="batch-track-name-input" /></div>
            <div className="space-y-1.5"><Label>Pickup Tracking Number <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={trackForm.pickup_tracking_number} onChange={(e) => setTrackForm((f) => ({ ...f, pickup_tracking_number: e.target.value }))} placeholder="e.g. DL123456789" data-testid="batch-track-number-input" /></div>
          </div>
          <DialogFooter><Button onClick={doBatchTracking} disabled={saving || !trackForm.domestic_courier} data-testid="confirm-batch-tracking">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Apply to Batch</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add shipments to batch */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="batch-add-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add Shipments</DialogTitle>
            <DialogDescription>Pick shipments to add to this batch. A shipment can only be in one batch.</DialogDescription></DialogHeader>
          <OrderPicker orders={available} selected={addPick} onToggle={toggleAddPick} emptyText="No eligible shipments to add." />
          <DialogFooter><Button onClick={addToBatch} disabled={saving || !addPick.length} data-testid="confirm-batch-add">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add {addPick.length || ""}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual shipment detail inside a batch */}
      <Dialog open={!!orderDetail} onOpenChange={(v) => { if (!v) setOrderDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-order-detail-dialog">
          {orderDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{orderDetail.tracking_number}</DialogTitle>
                <DialogDescription>{orderLabel(orderDetail)} · {orderDetail.destination_country}</DialogDescription>
              </DialogHeader>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>Pickup: {orderDetail.pickup_address}</div>
                <div>Deliver to: {orderDetail.delivery_address}</div>
              </div>
              {orderDetail.boxes?.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm space-y-1">
                  <div className="font-semibold">{orderDetail.box_count} box(es) · {orderDetail.weight} kg</div>
                  {orderDetail.boxes.map((b, i) => (
                    <div key={i} className="text-xs text-muted-foreground">Box {i + 1}: {b.length}×{b.width}×{b.height} cm · {b.weight} kg · {b.item_content}</div>
                  ))}
                </div>
              )}
              {orderDetail.domestic_courier && (
                <div className="text-sm flex items-center gap-1.5"><Truck className="h-4 w-4 text-primary" /> {orderDetail.domestic_courier}{orderDetail.pickup_tracking_number ? ` · ${orderDetail.pickup_tracking_number}` : ""}</div>
              )}
              <Timeline timeline={orderDetail.timeline} status={orderDetail.status} order={orderDetail} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
